// src/components/acciones/BoletaPreview.js
import React, {
  memo,
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import './AccionesSection.css';
import CurvedText from './CurvedText';

// Constante `BOLETA_BLUE` que centraliza un valor de configuración compartido.
const BOLETA_BLUE = '#2b3990';

// Ajustes manuales de la boleta horizontal en el modal admin y DocuSign.
// Los porcentajes se calculan sobre el lienzo nativo de 670 x 450 px.
const ADMIN_HORIZONTAL_PREVIEW_LAYOUT = {
  frontText: {
    fontSize: 8,
    top: '62%',
    bottom: '9%',
    left: '20%',
    width: '60%',
    signatureBottom: '14%',
  },
  backText: {
    fontSize: '7px',
    lineHeight: 1.3,
    titleFontSize: '8px',
    top: '24%',
    leftParagraph: { left: '16%', width: '24%' },
    rightParagraph: { left: '60%', width: '24%' },
  },
  shieldSize: 530,
  waveWidth: 100,
};

// Constante `SVG_BASE` que centraliza un valor de configuración compartido.
const SVG_BASE = process.env.PUBLIC_URL + '/designs';

// Constante `FIXED_LAYERS` que centraliza un valor de configuración compartido.
const FIXED_LAYERS = {
  base: `${SVG_BASE}/1.svg`,
  innerDecor: `${SVG_BASE}/3.svg`,
  innerDecorHorizontal: `${SVG_BASE}/3_H.svg`,
  glowBorder: `${SVG_BASE}/4.svg`,
  sealCircle: `${SVG_BASE}/6.svg`,
  cityscape2: `${SVG_BASE}/8.svg`,
  dataFields: `${SVG_BASE}/12.svg`, // Mantenemos el fondo del membrete
};

// Constante `BACK_LAYERS` que centraliza un valor de configuración compartido.
const BACK_LAYERS = {
  base: `${SVG_BASE}/1B.svg`,
  banners: `${SVG_BASE}/2B.svg`,
  innerDecor: `${SVG_BASE}/3B.svg`,
  glowBorder: `${SVG_BASE}/4.svg`,
  content: `${SVG_BASE}/5B.svg`,
  shield: `${SVG_BASE}/escudo.svg`,
};

// ================= VARIABLES DEL MEMBRETE =================
export const DEFAULT_MEMBRETE_DOMICILIO = 'Indefinido';
// Constante `DEFAULT_MEMBRETE_DURACION` que centraliza un valor de configuración compartido.
export const DEFAULT_MEMBRETE_DURACION = 'Indefinida';
// Constante `DEFAULT_MEMBRETE_CAP_MIN` que centraliza un valor de configuración compartido.
export const DEFAULT_MEMBRETE_CAP_MIN = '$ __________________ M.N.';
// Constante `DEFAULT_MEMBRETE_CAP_VAR` que centraliza un valor de configuración compartido.
export const DEFAULT_MEMBRETE_CAP_VAR = '$ __________________ M.N.';

// Constante `PLACEHOLDERS` que centraliza un valor de configuración compartido.
export const PLACEHOLDERS = {
  titular: '{{titular}}',
  nacionalidad: '{{nacionalidad}}',
  domicilio: '{{domicilio}}',
  seriesAcciones: '{{seriesAcciones}}',
  seriesTotales: '{{seriesTotales}}',
  valorNominal: '{{valorNominal}}',
  escrituraNumero: '{{escrituraNumero}}',
  escrituraFecha: '{{escrituraFecha}}',
  notarioNombre: '{{notarioNombre}}',
  notarioNumero: '{{notarioNumero}}',
  notarioCiudad: '{{notarioCiudad}}',
  notarioEstado: '{{notarioEstado}}',
  folioMercantil: '{{folioMercantil}}',
  registroFecha: '{{registroFecha}}',
  emisionCiudad: '{{emisionCiudad}}',
  emisionEstado: '{{emisionEstado}}',
  emisionFecha: '{{emisionFecha}}',
};

// Constante `DEFAULT_PARRAFO1` que centraliza un valor de configuración compartido.
export const DEFAULT_PARRAFO1 = `El presente título definitivo se expide a favor del señor {{titular}}, de nacionalidad {{nacionalidad}}, con domicilio en {{domicilio}}, quien es titular de {{seriesAcciones}}, ordinarias, nominativas, liberadas, con valor nominal de {{valorNominal}} Moneda Nacional cada una, íntegramente suscritas y pagadas, de las {{seriesTotales}}, en las que a la fecha se divide el capital social de la Sociedad.`;

// Constante `DEFAULT_PARRAFO2` que centraliza un valor de configuración compartido.
export const DEFAULT_PARRAFO2 = `La Sociedad se constituyó mediante escritura pública número {{escrituraNumero}} de fecha {{escrituraFecha}}, otorgada ante la fe del Lic. {{notarioNombre}}, Notario Público Número {{notarioNumero}} de la Ciudad de {{notarioCiudad}}, {{notarioEstado}}, la cual quedó inscrita en el Registro Público de la Propiedad y del Comercio del mismo estado, bajo el Folio Mercantil Electrónico Número {{folioMercantil}}, con fecha {{registroFecha}}.`;

// Constante `DEFAULT_FECHA_LUGAR` que centraliza un valor de configuración compartido.
export const DEFAULT_FECHA_LUGAR = `{{emisionCiudad}}, {{emisionEstado}}, a {{emisionFecha}}.`;

// Constante `DEFAULT_REVERSO_PARRAFO1` que centraliza un valor de configuración compartido.
export const DEFAULT_REVERSO_PARRAFO1 = `Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer nec odio. Praesent libero. Sed cursus ante dapibus diam. Sed nisi. Nulla quis sem at nibh elementum imperdiet. Duis sagittis ipsum. Praesent mauris. Fusce nec tellus sed augue semper porta. Mauris massa. Vestibulum lacinia arcu eget nulla. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer nec odio. Praesent libero. Sed cursus ante dapibus diam. Sed nisi. Nulla quis sem at nibh elementum imperdiet. Duis sagittis ipsum. Praesent mauris. Fusce nec tellus sed augue semper porta. Mauris massa. Vestibulum lacinia arcu eget nulla. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos.Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer nec odio. Praesent libero. Sed cursus ante dapibus diam. Sed nisi. Nulla quis sem at nibh elementum imperdiet. Duis sagittis ipsum. Praesent mauris. Fusce nec tellus sed augue semper porta. Mauris massa. Vestibulum lacinia arcu eget nulla. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos.`;

// Constante `DEFAULT_REVERSO_PARRAFO2` que centraliza un valor de configuración compartido.
export const DEFAULT_REVERSO_PARRAFO2 = `Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer nec odio. Praesent libero. Sed cursus ante dapibus diam. Sed nisi. Nulla quis sem at nibh elementum imperdiet. Duis sagittis ipsum. Praesent mauris. Fusce nec tellus sed augue semper porta. Mauris massa. Vestibulum lacinia arcu eget nulla. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer nec odio. Praesent libero. Sed cursus ante dapibus diam. Sed nisi. Nulla quis sem at nibh elementum imperdiet. Duis sagittis ipsum. Praesent mauris. Fusce nec tellus sed augue semper porta. Mauris massa. Vestibulum lacinia arcu eget nulla. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos.Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer nec odio. Praesent libero. Sed cursus ante dapibus diam. Sed nisi. Nulla quis sem at nibh elementum imperdiet. Duis sagittis ipsum. Praesent mauris. Fusce nec tellus sed augue semper porta. Mauris massa. Vestibulum lacinia arcu eget nulla. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos.`;

// Función `formatDateToText` que transforma un valor al formato requerido.
const formatDateToText = (isoDate) => {
  if (!isoDate) return '______ de __________ del ______';
  // Valor intermedio `date` utilizado por la lógica de este bloque.
  const date = new Date(isoDate + 'T00:00:00');
  // Colección `meses` que reúne los elementos utilizados en este flujo.
  const meses =[
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
  ];
  return `${date.getDate()} de ${meses[date.getMonth()]} del ${date.getFullYear()}`;
};

// Función `buildSeriesText` que construye la estructura o el valor requerido.
const buildSeriesText = (series =[]) => {
  // Valor intermedio `enabledSeries` utilizado por la lógica de este bloque.
  const enabledSeries = series.filter((s) => s.enabled && s.cantidad);
  if (enabledSeries.length === 0) return '____________ acciones Serie "___"';
  return enabledSeries
    .map((s) => `${s.cantidad || '______'} acciones Serie "${s.nombre}"`)
    .join(' y ');
};

// Función `buildSeriesTotalText` que construye la estructura o el valor requerido.
const buildSeriesTotalText = (series =[]) => {
  // Valor intermedio `enabledSeries` utilizado por la lógica de este bloque.
  const enabledSeries = series.filter((s) => s.enabled && s.total);
  if (enabledSeries.length === 0) return '____________ acciones Serie "___"';
  return enabledSeries
    .map((s) => `${s.total || '______'} acciones Serie "${s.nombre}"`)
    .join(' y ');
};

// Función `decodeHtmlEntities` que transforma un valor al formato requerido.
const decodeHtmlEntities = (str) => {
  if (!str || typeof str !== 'string') return str;
  // Valor intermedio `decoded` utilizado por la lógica de este bloque.
  let decoded = str;
  // Valor intermedio `previous` utilizado por la lógica de este bloque.
  let previous = '';
  while (decoded !== previous) {
    previous = decoded;
    decoded = decoded
      .replace(/&amp;/g, '&')
      .replace(/&#x2F;/g, '/')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#x27;/g, "'")
      .replace(/&#x60;/g, '`')
      .replace(/&#x3D;/g, '=')
      .replace(/&#39;/g, "'");
  }
  return decoded;
};

// Divide un texto en varias líneas en función del ancho disponible (px) y del
// tamaño de letra. Cuanto mayor sea la letra, menos caracteres caben por línea
// y se generan más renglones. Sirve para envolver el nombre de la empresa en el
// reverso sin recortar el texto ni achicar la fuente arbitrariamente.
const wrapTextByWidth = (text, fontSize, availableWidth, letterSpacing = 1.5) => {
  if (!text) return [];
  // Valor intermedio `safeWidth` utilizado por la lógica de este bloque.
  const safeWidth = Math.max(8, availableWidth);
  // Valor intermedio `charWidth` utilizado por la lógica de este bloque.
  const charWidth = fontSize * 0.62 + letterSpacing;
  // Valor intermedio `maxCharsPerLine` utilizado por la lógica de este bloque.
  const maxCharsPerLine = Math.max(1, Math.floor((safeWidth * 0.85) / charWidth));
  // Valor intermedio `words` utilizado por la lógica de este bloque.
  const words = String(text).split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  // Colección `lines` que reúne los elementos utilizados en este flujo.
  const lines = [];
  // Valor intermedio `current` utilizado por la lógica de este bloque.
  let current = '';
  for (const word of words) {
    // Si una sola palabra excede el renglón, se parte por caracteres.
    if (word.length > maxCharsPerLine) {
      if (current) { lines.push(current); current = ''; }
      for (let i = 0; i < word.length; i += maxCharsPerLine) {
        lines.push(word.slice(i, i + maxCharsPerLine));
      }
      continue;
    }
    // Valor intermedio `candidate` utilizado por la lógica de este bloque.
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length > maxCharsPerLine && current) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }
  if (current) lines.push(current);
  return lines;
};


// Función `replacePlaceholders` que transforma un valor al formato requerido.
export const replacePlaceholders = (text, adminFields = {}, series =[]) => {
  if (!text) return '';
  // Configuración `decoded` que agrupa valores relacionados.
  const decoded = {};
  Object.entries(adminFields).forEach(([k, v]) => {
    decoded[k] = typeof v === 'string' ? decodeHtmlEntities(v) : v;
  });
  // Configuración `replacements` que agrupa valores relacionados.
  const replacements = {
    '{{titular}}': decoded.titularNombre || '______________________',
    '{{nacionalidad}}': decoded.titularNacionalidad || '____________',
    '{{domicilio}}': decoded.titularDomicilio || '________________________',
    '{{seriesAcciones}}': buildSeriesText(series),
    '{{seriesTotales}}': buildSeriesTotalText(series),
    '{{valorNominal}}': decoded.valorNominal || '$________ (_______________ pesos 00/100)',
    '{{escrituraNumero}}': decoded.escrituraNumero || '________',
    '{{escrituraFecha}}': formatDateToText(decoded.escrituraFecha),
    '{{notarioNombre}}': decoded.notarioNombre || '________________________',
    '{{notarioNumero}}': decoded.notarioNumero || '______',
    '{{notarioCiudad}}': decoded.notarioCiudad || '____________',
    '{{notarioEstado}}': decoded.notarioEstado || '____________',
    '{{folioMercantil}}': decoded.folioMercantil || '_______________',
    '{{registroFecha}}': formatDateToText(decoded.registroFecha),
    '{{emisionCiudad}}': decoded.emisionCiudad || '____________',
    '{{emisionEstado}}': decoded.emisionEstado || '______',
    '{{emisionFecha}}': formatDateToText(decoded.emisionFecha),
  };
  // Valor intermedio `result` utilizado por la lógica de este bloque.
  let result = decodeHtmlEntities(text);
  Object.entries(replacements).forEach(([placeholder, value]) => {
    result = result.replace(
      new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'),
      value
    );
  });
  return result;
};

// Constante `CANONICAL` que centraliza un valor de configuración compartido.
const CANONICAL = {
  vertical: { width: 450, height: 670 },
  horizontal: { width: 670, height: 450 },
};

// Constante `CONFIG` que centraliza un valor de configuración compartido.
const CONFIG = {
  vertical: {
    preview: { width: 450, height: 670 },
    marco: { scale: 100 },
    logo: { top: 5, left: 50, width: 90, maxHeight: 700 },
    companyName: {
      top: 19,
      fontSize: 13,
      width: 350,
      height: 55,
      curveIntensity: 40,
      maxCharsPerLine: 35,
      lineSpacing: -0.25,
    },
    membretes: {
      // -----------------------------------------------------------
      // 🎛️ CONTROLES DEL MEMBRETE DERECHO (ORIENTACIÓN VERTICAL)
      // -----------------------------------------------------------
      top: '48.2%',         // Subir/bajar (Ej: '49%' baja más)
      left: '58%',        // Mover a la izq/der (Ej: '60%' va a la derecha)
      width: '24%',         // Ancho de la caja
      fontSize: '5px',    // Tamaño de la letra
      lineHeight: 1.8,      // Separación entre renglones (1.8 es amplio)
      offsetX: 0,       // Desplazamiento horizontal (px). Negativo = hacia el centro
      // -----------------------------------------------------------
    },
    textOverlay: {
      top: '62%',
      left: '15%',
      width: '70%',
      bottom: '14%', 
      fontSize: '6.5px',
      lineHeight: 1.28,
      gap: '8px', 
      signatureFontSize: '6.5px',
      signatureLineWidth: '0.8px',
      signatureGap: '45px', 
      signaturePadding: '0 4% 0 18%',
      singleSignatureWidth: '45%',
      doubleSignatureWidth: '42%',
    },
    reversoShield: {
      width: 500,
      height: 500,
      top: '50%',
      left: '50%',
      rotate: 90, // Volteado 180 grados (de -90 a 90) para coincidir con el texto
    },
    reversoOverlay: {
      fontSize: '6px',
      lineHeight: 1.35,
      titleFontSize: '7.5px',
      parrafo1: {
        titulo: 'LOREM IPSUM',
        top: '18%',  // Ajustado para compensar el giro de la caja rotada a 90deg
        left: '72%', // Ajustado para que crezca hacia la izquierda ocupando el mismo espacio
        width: '30%',
      },
      parrafo2: {
        titulo: 'LOREM IPSUM',
        top: '62%',  // Ajustado para compensar el giro de la caja rotada a 90deg
        left: '72%', // Mismo nivel vertical que el primer párrafo después de la rotación
        width: '30%',
      },
    },
    reversoCompanyName: {
      left: '55%',
      top: '50%',
      fontSize: 7,
      width: 80,
      height: 42,
      curveIntensity: 40,
      maxCharsPerLine: 35,
      lineSpacing: -0.25,
      lineGap: -16,
    },
  },
  horizontal: {
    preview: { width: 670, height: 450 },
    marco: { scale: 100 },
    logo: { top: 8, left: 50, width: 30 },
    companyName: {
      top: 11,
      fontSize: 11.5,
      width: 480,
      height: 55,
      curveIntensity: 36,
      maxCharsPerLine: 35,
      lineSpacing: -0.25,
    },
    membretes: {
      // -----------------------------------------------------------
      // 🎛️ CONTROLES DEL MEMBRETE DERECHO (ORIENTACIÓN HORIZONTAL)
      // -----------------------------------------------------------
      top: '47.7%',           // Subir/bajar (Modificado para alinear)
      left: '53.5%',          // Mover a la izq/der
      width: '17%',         // Ancho de la caja
      fontSize: '5px',      // Tamaño de la letra (Reducido para caber)
      lineHeight: 1.6,      // Separación entre renglones (Más pegados)
      offsetX: -15,       // Desplazamiento horizontal (px). Negativo = hacia el centro
      // -----------------------------------------------------------
    },
    textOverlay: {
      top: '59%',
      left: '27%',
      width: '45%',
      bottom: '11.5%',
      fontSize: '6px',
      lineHeight: 1.18,
      gap: '6px', 
      signatureFontSize: '6.5px',
      signatureLineWidth: '0.7px',
      signatureGap: '4px',
      singleSignatureWidth: '32%', 
      doubleSignatureWidth: '30%',
      sealFloat: {
        enabled: true,
        floatWidth: 125,
        floatHeight: 114,
        marginTop: 27,
        shapeRadius: 61,
        shapeX: '100%',
        shapeY: '100%',
        shapeMargin: 10,
      },
      signaturePosition: {
        left: '20%',   
        width: '62%',
        bottom: '19%', 
      },
    },
    reversoShield: {
      width: 365,
      height: 365,
      top: '50%',
      left: '50%',
      rotate: 0,
    },
    reversoOverlay: {
      fontSize: '6px',
      lineHeight: 1.3,
      titleFontSize: '8px',
      parrafo1: {
        titulo: 'LOREM IPSUM',
        top: '28%',
        left: '23.5%',
        width: '19%',
      },
      parrafo2: {
        titulo: 'LOREM IPSUM',
        top: '28%',
        left: '57.5%',
        width: '19%',
      },
    },
    reversoCompanyName: {
      left: '50%',
      top: '45%',
      fontSize: 6,
      width: 50,
      height: 35,
      curveIntensity: 20,
      maxCharsPerLine: 25,
      lineSpacing: -0.25,
      lineGap: -16,
    },
  },
};

// Valor intermedio `ProtectedImg` utilizado por la lógica de este bloque.
const ProtectedImg = memo(function ProtectedImg({ src, alt, style, className = '' }) {
  // Estado local `loaded` y su actualizador, usados para reflejar cambios en la interfaz.
  const[loaded, setLoaded] = useState(false);
  // Estado local `error` y su actualizador, usados para reflejar cambios en la interfaz.
  const [error, setError] = useState(false);
  // Callback `handleLoad` memoizado para conservar su referencia entre renderizados.
  const handleLoad = useCallback(() => setLoaded(true),[]);
  // Callback `handleError` memoizado para conservar su referencia entre renderizados.
  const handleError = useCallback(() => setError(true),[]);
  
  if (!src || error) return null;
  
  return (
    <img
      src={src}
      alt={alt}
      draggable="false"
      className={`wisby-force-print-img ${className}`.trim()}
      style={{
        pointerEvents: 'none',
        userSelect: 'none',
        opacity: loaded ? 1 : 0,
        transition: 'opacity 0.15s ease',
        ...style,
      }}
      onLoad={handleLoad}
      onError={handleError}
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
    />
  );
});

// Valor intermedio `RotatableLayer` utilizado por la lógica de este bloque.
const RotatableLayer = memo(function RotatableLayer({
  src, zIndex, scale = 100, widthScale, heightScale, offsetY = 0, offsetX = 0, rotate = 0,
}) {
  return (
    <ProtectedImg
      src={src}
      alt=""
      style={{
        position: 'absolute',
        top: `calc(50% + ${offsetY}px)`,
        left: `calc(50% + ${offsetX}px)`,
        transform: `translate(-50%, -50%) rotate(${rotate}deg)`,
        width: `${widthScale ?? scale}%`,
        height: `${heightScale ?? scale}%`,
        objectFit: 'fill',
        zIndex,
      }}
    />
  );
});

// Valor intermedio `RotatableHueLayer` utilizado por la lógica de este bloque.
const RotatableHueLayer = memo(function RotatableHueLayer({
  src, zIndex, scale = 100, widthScale, heightScale, offsetY = 0, offsetX = 0, rotate = 0, className = '',
}) {
  // Estado local `loaded` y su actualizador, usados para reflejar cambios en la interfaz.
  const[loaded, setLoaded] = useState(false);
  // Estado local `error` y su actualizador, usados para reflejar cambios en la interfaz.
  const [error, setError] = useState(false);
  // Callback `handleLoad` memoizado para conservar su referencia entre renderizados.
  const handleLoad = useCallback(() => setLoaded(true),[]);
  // Callback `handleError` memoizado para conservar su referencia entre renderizados.
  const handleError = useCallback(() => setError(true),[]);
  
  if (!src || error) return null;
  
  return (
    <img
      src={src}
      alt=""
      draggable="false"
      className={`wisby-force-print-img ${className}`.trim()}
      style={{
        position: 'absolute',
        top: `calc(50% + ${offsetY}px)`,
        left: `calc(50% + ${offsetX}px)`,
        transform: `translate(-50%, -50%) rotate(${rotate}deg)`,
        width: `${widthScale ?? scale}%`,
        height: `${heightScale ?? scale}%`,
        objectFit: 'fill',
        zIndex,
        pointerEvents: 'none',
        userSelect: 'none',
        opacity: loaded ? 1 : 0,
        transition: 'opacity 0.15s ease',
        mixBlendMode: 'multiply',
      }}
      onLoad={handleLoad}
      onError={handleError}
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
    />
  );
});

// Valor intermedio `SignatureBlock` utilizado por la lógica de este bloque.
const SignatureBlock = memo(function SignatureBlock({
  signatureCount = 1, 
  firmaIzquierda, 
  firmaDerecha, 
  fontSize, 
  lineWidth, 
  textColor, 
  gap, 
  style: externalStyle = {},
  singleWidth = '45%', 
  doubleWidth = '42%'
}) {
  if (signatureCount === 1) {
    return (
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '0 6%', flexShrink: 0, marginTop: gap, ...externalStyle }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: singleWidth }}>
          <div style={{ width: '100%', borderBottom: `${lineWidth} solid ${textColor}`, marginBottom: '2px' }} />
          <span style={{ fontSize, color: textColor, fontFamily: '"Times New Roman", Georgia, serif', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
            {firmaIzquierda}
          </span>
        </div>
      </div>
    );
  }
  return (
    <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', padding: '0 6%', flexShrink: 0, marginTop: gap, ...externalStyle }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: doubleWidth }}>
        <div style={{ width: '100%', borderBottom: `${lineWidth} solid ${textColor}`, marginBottom: '2px' }} />
        <span style={{ fontSize, color: textColor, fontFamily: '"Times New Roman", Georgia, serif', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
          {firmaIzquierda}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: doubleWidth }}>
        <div style={{ width: '100%', borderBottom: `${lineWidth} solid ${textColor}`, marginBottom: '2px' }} />
        <span style={{ fontSize, color: textColor, fontFamily: '"Times New Roman", Georgia, serif', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
          {firmaDerecha}
        </span>
      </div>
    </div>
  );
});

// Valor intermedio `MembretesOverlay` utilizado por la lógica de este bloque.
const MembretesOverlay = memo(function MembretesOverlay({
  adminFields = {}, orientation = 'vertical', isThumbnail = false
}) {
  if (isThumbnail) return null;
  // Valor intermedio `config` utilizado por la lógica de este bloque.
  const config = CONFIG[orientation]?.membretes;
  if (!config) return null;

  // Función `formatCapital` que transforma un valor al formato requerido.
  const formatCapital = (val, defaultVal) => {
    if (!val || String(val).trim() === '') return defaultVal;
    // Valor intermedio `text` utilizado por la lógica de este bloque.
    let text = decodeHtmlEntities(String(val)).trim();
    
    // Indicador booleano `isNumeric` que representa la condición señalada por su nombre.
    const isNumeric = /^[\d,.]+$/.test(text);
    if (isNumeric) {
      // Valor intermedio `cleanNum` utilizado por la lógica de este bloque.
      const cleanNum = text.replace(/,/g, '');
      if (!isNaN(cleanNum) && cleanNum !== '') {
        // Valor intermedio `parts` utilizado por la lógica de este bloque.
        const parts = cleanNum.split('.');
        // Valor intermedio `intPart` utilizado por la lógica de este bloque.
        const intPart = parseInt(parts[0] || '0', 10).toLocaleString('en-US');
        // Valor intermedio `decPart` utilizado por la lógica de este bloque.
        const decPart = parts.length > 1 ? `.${parts[1]}` : '';
        text = `${intPart}${decPart}`;
      }
    }

    if (!text.includes('$')) text = `$ ${text}`;
    if (!text.toUpperCase().includes('M.N.')) text = `${text} M.N.`;

    return text;
  };

  // Valor intermedio `dom` utilizado por la lógica de este bloque.
  const dom = decodeHtmlEntities(adminFields.domicilioSocial) || DEFAULT_MEMBRETE_DOMICILIO;
  // Valor intermedio `dur` utilizado por la lógica de este bloque.
  const dur = decodeHtmlEntities(adminFields.duracion) || DEFAULT_MEMBRETE_DURACION;
  // Valor intermedio `capMin` utilizado por la lógica de este bloque.
  const capMin = formatCapital(adminFields.capitalMinimo, DEFAULT_MEMBRETE_CAP_MIN);
  // Valor intermedio `capVar` utilizado por la lógica de este bloque.
  const capVar = formatCapital(adminFields.capitalVariable, DEFAULT_MEMBRETE_CAP_VAR);

  // Configuración `lineStyle` que agrupa valores relacionados.
  const lineStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  return (
    <div style={{
      position: 'absolute',
      top: config.top,
      left: config.left,     // Se alimenta directamente del CONFIG
      width: config.width,   // Se alimenta directamente del CONFIG
      fontSize: config.fontSize,
      lineHeight: config.lineHeight,
      fontFamily: '"Times New Roman", "Book Antiqua", Georgia, serif',
      color: '#1a1a2e',
      zIndex: 22,
      pointerEvents: 'none',
      display: 'flex',
      flexDirection: 'column',
      textAlign: 'center',
      fontWeight: 600,
      transform: `translateX(${config.offsetX || 0}px)`,
    }}>
      <div style={lineStyle}>{dom}</div>
      <div style={lineStyle}>{dur}</div>
      <div style={lineStyle}>{capMin}</div>
      <div style={lineStyle}>{capVar}</div>
    </div>
  );
});

// Valor intermedio `DynamicTextOverlay` utilizado por la lógica de este bloque.
const DynamicTextOverlay = memo(function DynamicTextOverlay({
  adminFields = {}, series =[], textoLegalOverride = null, orientation = 'vertical', isThumbnail = false,
  adminEditorPreview = false,
}) {
  // Valor intermedio `config` utilizado por la lógica de este bloque.
  const config = CONFIG[orientation];
  // Valor intermedio `tc` utilizado por la lógica de este bloque.
  const tc = config.textOverlay;
  // Constante `TEXT_COLOR` que centraliza un valor de configuración compartido.
  const TEXT_COLOR = '#1a1a2e';
  // Indicador booleano `isHorizontal` que representa la condición señalada por su nombre.
  const isHorizontal = orientation === 'horizontal';
  // Valor intermedio `useAdminHorizontalLayout` utilizado por la lógica de este bloque.
  const useAdminHorizontalLayout = isHorizontal && adminEditorPreview;
  // Valor intermedio `configuredFontSize` utilizado por la lógica de este bloque.
  const configuredFontSize = useAdminHorizontalLayout
    ? ADMIN_HORIZONTAL_PREVIEW_LAYOUT.frontText.fontSize
    : parseFloat(tc.fontSize);

  // Referencia mutable `textContainerRef` que conserva datos entre renderizados sin provocar uno nuevo.
  const textContainerRef = useRef(null);
  // Estado local `adjustedFontSize` y su actualizador, usados para reflejar cambios en la interfaz.
  const [adjustedFontSize, setAdjustedFontSize] = useState(configuredFontSize);
  // Referencia mutable `iterationRef` que conserva datos entre renderizados sin provocar uno nuevo.
  const iterationRef = useRef(0);

  // Valor intermedio `parrafo1Template` utilizado por la lógica de este bloque.
  const parrafo1Template = textoLegalOverride?.useCustomText ? textoLegalOverride.parrafo1Custom || DEFAULT_PARRAFO1 : DEFAULT_PARRAFO1;
  // Valor intermedio `parrafo2Template` utilizado por la lógica de este bloque.
  const parrafo2Template = textoLegalOverride?.useCustomText ? textoLegalOverride.parrafo2Custom || DEFAULT_PARRAFO2 : DEFAULT_PARRAFO2;
  // Valor intermedio `fechaLugarTemplate` utilizado por la lógica de este bloque.
  const fechaLugarTemplate = textoLegalOverride?.useCustomText ? textoLegalOverride.fechaLugarCustom || DEFAULT_FECHA_LUGAR : DEFAULT_FECHA_LUGAR;

  // Valor `parrafo1` memoizado para recalcularlo solo cuando cambian sus dependencias.
  const parrafo1 = useMemo(() => replacePlaceholders(parrafo1Template, adminFields, series),[parrafo1Template, adminFields, series]);
  // Valor `parrafo2` memoizado para recalcularlo solo cuando cambian sus dependencias.
  const parrafo2 = useMemo(() => replacePlaceholders(parrafo2Template, adminFields, series),[parrafo2Template, adminFields, series]);
  // Valor `fechaLugar` memoizado para recalcularlo solo cuando cambian sus dependencias.
  const fechaLugar = useMemo(() => replacePlaceholders(fechaLugarTemplate, adminFields, series),[fechaLugarTemplate, adminFields, series]);

  // Valor intermedio `firmaIzquierda` utilizado por la lógica de este bloque.
  const firmaIzquierda = decodeHtmlEntities(textoLegalOverride?.firmaIzquierda || adminFields.firmaIzquierda || 'CONSEJERO');
  // Valor intermedio `firmaDerecha` utilizado por la lógica de este bloque.
  const firmaDerecha = decodeHtmlEntities(textoLegalOverride?.firmaDerecha || adminFields.firmaDerecha || 'CONSEJERO');
  
  // Valor intermedio `signatureCount` utilizado por la lógica de este bloque.
  const signatureCount = textoLegalOverride?.useCustomText 
    ? (textoLegalOverride.signatureCount || 1) 
    : (adminFields.signatureCount || 1);

  useEffect(() => {
    iterationRef.current = 0;
    setAdjustedFontSize(configuredFontSize);
  },[parrafo1, parrafo2, fechaLugar, configuredFontSize]);

  useEffect(() => {
    // Valor intermedio `el` utilizado por la lógica de este bloque.
    const el = textContainerRef.current;
    if (!el) return;
    // Valor intermedio `baseFontSize` utilizado por la lógica de este bloque.
    const baseFontSize = configuredFontSize;
    // Valor intermedio `minFontSize` utilizado por la lógica de este bloque.
    const minFontSize = baseFontSize * (isHorizontal ? 0.9 : 0.55);
    // Valor intermedio `rafId` utilizado por la lógica de este bloque.
    const rafId = requestAnimationFrame(() => {
      if (!el) return;
      // Indicador booleano `hasVerticalOverflow` que representa la condición señalada por su nombre.
      const hasVerticalOverflow = el.scrollHeight > el.clientHeight + 1;
      if (hasVerticalOverflow && adjustedFontSize > minFontSize && iterationRef.current < 30) {
        iterationRef.current += 1;
        setAdjustedFontSize((prev) => Math.max(prev - (isHorizontal ? 0.03 : 0.08), minFontSize));
      }
    });
    return () => cancelAnimationFrame(rafId);
  }, [adjustedFontSize, configuredFontSize, isHorizontal]);

  if (isThumbnail) return null;

  // Configuración `paragraphStyle` que agrupa valores relacionados.
  const paragraphStyle = {
    margin: 0, padding: 0,
    maxWidth: '100%', boxSizing: 'border-box',
    fontSize: `${adjustedFontSize}px`,
    lineHeight: tc.lineHeight,
    color: TEXT_COLOR,
    fontFamily: '"Times New Roman", "Book Antiqua", "Palatino Linotype", Palatino, Georgia, serif',
    textAlign: 'justify', textAlignLast: 'auto',
    whiteSpace: 'normal', wordBreak: 'normal',
    overflowWrap: isHorizontal ? 'anywhere' : 'break-word',
    hyphens: 'none',
    letterSpacing: isHorizontal ? '0em' : '0.01em',
    fontWeight: 400,
  };

  // Valor intermedio `sealFloat` utilizado por la lógica de este bloque.
  const sealFloat = tc.sealFloat;
  // Indicador booleano `showSealFloat` que representa la condición señalada por su nombre.
  const showSealFloat = isHorizontal && sealFloat?.enabled;
  // Valor intermedio `sigPos` utilizado por la lógica de este bloque.
  const sigPos = useAdminHorizontalLayout
    ? {
        ...tc.signaturePosition,
        bottom: ADMIN_HORIZONTAL_PREVIEW_LAYOUT.frontText.signatureBottom,
      }
    : tc.signaturePosition;
  // Valor intermedio `textTop` utilizado por la lógica de este bloque.
  const textTop = useAdminHorizontalLayout
    ? ADMIN_HORIZONTAL_PREVIEW_LAYOUT.frontText.top
    : tc.top;
  // Valor intermedio `textBottom` utilizado por la lógica de este bloque.
  const textBottom = useAdminHorizontalLayout
    ? ADMIN_HORIZONTAL_PREVIEW_LAYOUT.frontText.bottom
    : tc.bottom;
  // Valor intermedio `textLeft` utilizado por la lógica de este bloque.
  const textLeft = useAdminHorizontalLayout
    ? ADMIN_HORIZONTAL_PREVIEW_LAYOUT.frontText.left
    : tc.left;
  // Valor intermedio `textWidth` utilizado por la lógica de este bloque.
  const textWidth = useAdminHorizontalLayout
    ? ADMIN_HORIZONTAL_PREVIEW_LAYOUT.frontText.width
    : tc.width;

  if (showSealFloat) {
    // Valor intermedio `shapeOutsideValue` utilizado por la lógica de este bloque.
    const shapeOutsideValue = `circle(${sealFloat.shapeRadius}px at ${sealFloat.shapeX || '100%'} ${sealFloat.shapeY || '100%'})`;
    return (
      <>
        <div
          ref={textContainerRef}
          style={{
            position: 'absolute',
            top: textTop, left: textLeft, width: textWidth, bottom: textBottom,
            zIndex: 22, pointerEvents: 'none', userSelect: 'none',
            display: 'block', overflow: 'hidden',
          }}
        >
          <div
            style={{
              float: 'right',
              width: `${sealFloat.floatWidth}px`,
              height: `${sealFloat.floatHeight}px`,
              marginTop: `${sealFloat.marginTop || 0}px`,
              shapeOutside: shapeOutsideValue,
              WebkitShapeOutside: shapeOutsideValue,
              shapeMargin: `${sealFloat.shapeMargin}px`,
              WebkitShapeMargin: `${sealFloat.shapeMargin}px`,
              pointerEvents: 'none',
            }}
          />
          <p style={{ ...paragraphStyle, marginBottom: tc.gap }}>{parrafo1}</p>
          <p style={{ ...paragraphStyle, marginBottom: tc.gap }}>{parrafo2}</p>
          <p style={{ ...paragraphStyle, textAlign: 'center', marginBottom: tc.gap }}>{fechaLugar}</p>
        </div>
        <SignatureBlock
          signatureCount={signatureCount}
          firmaIzquierda={firmaIzquierda} firmaDerecha={firmaDerecha}
          fontSize={tc.signatureFontSize} lineWidth={tc.signatureLineWidth}
          textColor={TEXT_COLOR} gap="0px"
          style={{ position: 'absolute', left: sigPos.left, width: sigPos.width, bottom: sigPos.bottom, zIndex: 22, pointerEvents: 'none' }}
          singleWidth={tc.singleSignatureWidth}
          doubleWidth={tc.doubleSignatureWidth}
        />
      </>
    );
  }

  return (
    <div
      ref={textContainerRef}
      style={{
        position: 'absolute',
        top: textTop, left: textLeft, width: textWidth, bottom: textBottom,
        zIndex: 22, pointerEvents: 'none', userSelect: 'none',
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-start',
        gap: tc.gap, overflow: 'hidden',
      }}
    >
      <p style={paragraphStyle}>{parrafo1}</p>
      <p style={paragraphStyle}>{parrafo2}</p>
      <p style={{ ...paragraphStyle, textAlign: 'center' }}>{fechaLugar}</p>
      <SignatureBlock
        signatureCount={signatureCount}
        firmaIzquierda={firmaIzquierda} firmaDerecha={firmaDerecha}
        fontSize={tc.signatureFontSize} lineWidth={tc.signatureLineWidth}
        textColor={TEXT_COLOR} gap={tc.signatureGap}
        style={tc.signaturePadding ? { padding: tc.signaturePadding } : {}}
        singleWidth={tc.singleSignatureWidth}
        doubleWidth={tc.doubleSignatureWidth}
      />
    </div>
  );
});

// Valor intermedio `ReversoTextOverlay` utilizado por la lógica de este bloque.
const ReversoTextOverlay = memo(function ReversoTextOverlay({
  reversoOverride = null, orientation = 'vertical', isThumbnail = false,
  adminEditorPreview = false,
}) {
  // Valor intermedio `config` utilizado por la lógica de este bloque.
  const config = CONFIG[orientation];
  // Valor intermedio `tc` utilizado por la lógica de este bloque.
  const tc = config.reversoOverlay;
  // Constante `TEXT_COLOR` que centraliza un valor de configuración compartido.
  const TEXT_COLOR = BOLETA_BLUE;
  // Indicador booleano `isHorizontal` que representa la condición señalada por su nombre.
  const isHorizontal = orientation === 'horizontal';
  // Valor intermedio `useAdminHorizontalLayout` utilizado por la lógica de este bloque.
  const useAdminHorizontalLayout = isHorizontal && adminEditorPreview;

  // Valor intermedio `parrafo1` utilizado por la lógica de este bloque.
  const parrafo1 = decodeHtmlEntities(reversoOverride?.useCustomText
    ? reversoOverride.parrafo1Custom || DEFAULT_REVERSO_PARRAFO1
    : DEFAULT_REVERSO_PARRAFO1);
  // Valor intermedio `parrafo2` utilizado por la lógica de este bloque.
  const parrafo2 = decodeHtmlEntities(reversoOverride?.useCustomText
    ? reversoOverride.parrafo2Custom || DEFAULT_REVERSO_PARRAFO2
    : DEFAULT_REVERSO_PARRAFO2);

  // Mongoose puede guardar títulos no capturados como ''. En ese caso se
  // debe usar el título predeterminado, no renderizar un espacio vacío.
  const titulo1 = decodeHtmlEntities(reversoOverride?.titulo1 || tc.parrafo1.titulo || '');
  // Valor intermedio `titulo2` utilizado por la lógica de este bloque.
  const titulo2 = decodeHtmlEntities(reversoOverride?.titulo2 || tc.parrafo2.titulo || '');

  if (isThumbnail) return null;

  // Configuración `baseParagraphStyle` que agrupa valores relacionados.
  const baseParagraphStyle = {
    margin: 0, padding: 0,
    fontSize: useAdminHorizontalLayout
      ? ADMIN_HORIZONTAL_PREVIEW_LAYOUT.backText.fontSize
      : tc.fontSize,
    lineHeight: useAdminHorizontalLayout
      ? ADMIN_HORIZONTAL_PREVIEW_LAYOUT.backText.lineHeight
      : tc.lineHeight,
    color: TEXT_COLOR,
    fontFamily: '"Times New Roman", "Book Antiqua", Georgia, serif',
    textAlign: 'justify', wordBreak: 'normal',
    overflowWrap: 'break-word', hyphens: 'none',
    letterSpacing: '0.01em', fontWeight: 400,
    whiteSpace: 'pre-line',
  };

  // Configuración `titleStyle` que agrupa valores relacionados.
  const titleStyle = {
    display: 'block',
    width: '100%',
    textAlign: 'center',
    fontSize: useAdminHorizontalLayout
      ? ADMIN_HORIZONTAL_PREVIEW_LAYOUT.backText.titleFontSize
      : (tc.titleFontSize || '7px'),
    fontFamily: '"Times New Roman", "Book Antiqua", Georgia, serif',
    fontWeight: 700,
    letterSpacing: '2px',
    color: TEXT_COLOR,
    marginBottom: '4px',
    textTransform: 'uppercase',
    flexShrink: 0,
  };

  // Valor intermedio `p1cfg` utilizado por la lógica de este bloque.
  const p1cfg = useAdminHorizontalLayout
    ? {
        ...tc.parrafo1,
        top: ADMIN_HORIZONTAL_PREVIEW_LAYOUT.backText.top,
        ...ADMIN_HORIZONTAL_PREVIEW_LAYOUT.backText.leftParagraph,
      }
    : tc.parrafo1;
  // Valor intermedio `p2cfg` utilizado por la lógica de este bloque.
  const p2cfg = useAdminHorizontalLayout
    ? {
        ...tc.parrafo2,
        top: ADMIN_HORIZONTAL_PREVIEW_LAYOUT.backText.top,
        ...ADMIN_HORIZONTAL_PREVIEW_LAYOUT.backText.rightParagraph,
      }
    : tc.parrafo2;

  // 🔑 FIX DE IMPRESIÓN: el texto del reverso se renderiza FUERA del wrapper
  // rotado 180° (ver BoletaPreview). Para replicar el mismo resultado visual
  // con una SOLA rotación (y así evitar que el motor de impresión de Chrome
  // descarte el texto por rotaciones anidadas), en vertical invertimos la
  // posición (compensando el giro de 180° del wrapper) y aplicamos rotate(270deg)
  // (= 180° + 90°). En horizontal no hay giro del wrapper, se queda igual.
  const p1Resolved = isHorizontal
    ? p1cfg
    : {
        top: `${100 - parseFloat(p1cfg.top)}%`,
        left: `${100 - parseFloat(p1cfg.left)}%`,
        width: p1cfg.width,
      };
  // Valor intermedio `p2Resolved` utilizado por la lógica de este bloque.
  const p2Resolved = isHorizontal
    ? p2cfg
    : {
        top: `${100 - parseFloat(p2cfg.top)}%`,
        left: `${100 - parseFloat(p2cfg.left)}%`,
        width: p2cfg.width,
      };

  return (
    <>
      <div
        className="reverso-paragraph-block"
        style={{
          position: 'absolute',
          top: p1Resolved.top,
          left: p1Resolved.left,
          width: p1Resolved.width,
          zIndex: 22,
          pointerEvents: 'none',
          userSelect: 'none',
          display: 'flex',
          flexDirection: 'column',
          transform: isHorizontal ? 'none' : 'rotate(270deg)',
          transformOrigin: 'top left',
        }}
      >
        {titulo1 ? <span style={titleStyle}>{titulo1}</span> : null}
        <div className="reverso-paragraph-scroll">
          <p style={baseParagraphStyle}>
            {parrafo1}
          </p>
        </div>
      </div>

      <div
        className="reverso-paragraph-block"
        style={{
          position: 'absolute',
          top: p2Resolved.top,
          left: p2Resolved.left,
          width: p2Resolved.width,
          zIndex: 22,
          pointerEvents: 'none',
          userSelect: 'none',
          display: 'flex',
          flexDirection: 'column',
          transform: isHorizontal ? 'none' : 'rotate(270deg)',
          transformOrigin: 'top left',
        }}
      >
        {titulo2 ? <span style={titleStyle}>{titulo2}</span> : null}
        <div className="reverso-paragraph-scroll">
          <p style={baseParagraphStyle}>
            {parrafo2}
          </p>
        </div>
      </div>
    </>
  );
});

// Componente `BoletaPreview` que renderiza esta parte de la interfaz.
function BoletaPreview({
  boletaData,
  designUrls = {},
  isThumbnail = false,
  adminFields = {},
  series =[],
  textoLegalOverride = null,
  reversoOverride = null,
  showBack = false,
  boletaCode,
  previewTitleNudge = 0,
  adminEditorPreview = false,
}) {
  if (!boletaData) return null;

  // Valor intermedio `orientation` utilizado por la lógica de este bloque.
  const orientation = boletaData.orientation || 'vertical';
  // Valor intermedio `config` utilizado por la lógica de este bloque.
  const config = CONFIG[orientation];
  // Indicador booleano `isHorizontal` que representa la condición señalada por su nombre.
  const isHorizontal = orientation === 'horizontal';
  // Valor intermedio `useAdminHorizontalLayout` utilizado por la lógica de este bloque.
  const useAdminHorizontalLayout = isHorizontal && adminEditorPreview;
  // Valor intermedio `scale` utilizado por la lógica de este bloque.
  const scale = isThumbnail ? 0.5 : 1;
  // Valor intermedio `textColor` utilizado por la lógica de este bloque.
  const textColor = BOLETA_BLUE;

  // Valor intermedio `rootClipPath` utilizado por la lógica de este bloque.
  const rootClipPath = 'none';

  // Valor intermedio `containerStyle` utilizado por la lógica de este bloque.
  const containerStyle = isThumbnail
    ? {
      position: 'relative', width: '100%', height: '100%',
      backgroundColor: 'transparent', 
      overflow: 'hidden',
      borderRadius: '3px', boxShadow: 'none', pointerEvents: 'none',
      aspectRatio: isHorizontal ? '670 / 450' : '450 / 670',
      clipPath: rootClipPath, 
    }
    : {
      position: 'relative',
      width: `${config.preview.width}px`,
      height: `${config.preview.height}px`,
      backgroundColor: 'transparent', 
      overflow: 'hidden',
      borderRadius: '0px',
      boxShadow: isHorizontal ? 'none' : '0 4px 20px rgba(0,0,0,0.1)',
      clipPath: rootClipPath, 
    };

  // Valor intermedio `marcoUrl` utilizado por la lógica de este bloque.
  let marcoUrl = designUrls.marco || null;
  if (marcoUrl && !marcoUrl.includes('/marcos/')) {
    marcoUrl = marcoUrl.replace('/designs/', '/designs/marcos/');
  }

  // Valor intermedio `membreteUrl` utilizado por la lógica de este bloque.
  const membreteUrl = designUrls.membrete || null;
  // Valor intermedio `logoUrl` utilizado por la lógica de este bloque.
  const logoUrl = designUrls.logo || null;
  // Valor intermedio `layerScale` utilizado por la lógica de este bloque.
  const layerScale = config.marco.scale;

  // Valor intermedio `hDecorWidthScale` utilizado por la lógica de este bloque.
  const hDecorWidthScale = (CANONICAL.vertical.width / CANONICAL.horizontal.width) * 100;
  // Valor intermedio `hDecorHeightScale` utilizado por la lógica de este bloque.
  const hDecorHeightScale = (CANONICAL.vertical.height / CANONICAL.horizontal.height) * 100;

  if (showBack) {
    // Valor intermedio `backRot` utilizado por la lógica de este bloque.
    const backRot = isHorizontal ? 270 : 180; 
    
    // Valor intermedio `wScale` utilizado por la lógica de este bloque.
    const wScale = isHorizontal ? hDecorWidthScale : layerScale;
    // Valor intermedio `hScale` utilizado por la lógica de este bloque.
    const hScale = isHorizontal ? hDecorHeightScale : layerScale;

    // Tomamos la configuración aislada del escudo
    const shieldCfg = useAdminHorizontalLayout
      ? {
          ...config.reversoShield,
          width: ADMIN_HORIZONTAL_PREVIEW_LAYOUT.shieldSize,
          height: ADMIN_HORIZONTAL_PREVIEW_LAYOUT.shieldSize,
        }
      : config.reversoShield;

    return (
      <div
        style={containerStyle}
        className={`protected-content boleta-container-animated ${isHorizontal ? 'is-horizontal' : 'is-vertical'}`}
        onContextMenu={(e) => e.preventDefault()}
      >
        <RotatableLayer src={BACK_LAYERS.base} zIndex={0} widthScale={wScale} heightScale={hScale} rotate={backRot} />
        <RotatableLayer src={BACK_LAYERS.glowBorder} zIndex={2} widthScale={wScale} heightScale={hScale} rotate={backRot} />
        <ProtectedImg
          src={marcoUrl}
          alt="Marco reverso"
          style={{
            position: 'absolute', top: '50%', left: '50%',
            transform: `translate(-50%, -50%) rotate(${backRot}deg)`,
            width: `${wScale}%`,
            height: `${hScale}%`,
            objectFit: 'fill', zIndex: 3,
          }}
        />
        <RotatableLayer src={BACK_LAYERS.banners} zIndex={4} widthScale={wScale} heightScale={hScale} rotate={backRot} />
        <RotatableLayer src={BACK_LAYERS.innerDecor} zIndex={5} widthScale={wScale} heightScale={hScale} rotate={backRot} />

        <div className="reverso-content-wrapper" style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: isHorizontal ? '670px' : '450px',
          height: isHorizontal ? '450px' : '670px',
          transform: isHorizontal
            ? 'translate(-50%, -50%) rotate(0deg)'
            : 'translate(-50%, -50%) rotate(180deg)',
          pointerEvents: 'none',
          userSelect: 'none',
        }}>
          {/* El contenedor del escudo ahora usa los pixeles fijos y la rotación que pongas en CONFIG */}
          <div style={{
            position: 'absolute',
            top: shieldCfg.top,
            left: shieldCfg.left,
            width: `${shieldCfg.width}px`,
            height: `${shieldCfg.height}px`,
            transform: `translate(-50%, -50%) rotate(${shieldCfg.rotate}deg)`,
            zIndex: 5,
          }}>
            <ProtectedImg
              src={BACK_LAYERS.shield}
              alt="Escudo Reverso"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          </div>

          {/* 🏢 NOMBRE DE LA EMPRESA EN EL CENTRO DEL REVERSO (curva + contorno igual que el anverso) */}
          {boletaData?.companyName && (() => {
            // Valor intermedio `cnCfg` utilizado por la lógica de este bloque.
            const cnCfg = config.reversoCompanyName;
            // Valor intermedio `cnText` utilizado por la lógica de este bloque.
            const cnText = decodeHtmlEntities(boletaData.companyName)?.toUpperCase() || 'NOMBRE DE EMPRESA';
            // Valor intermedio `ls` utilizado por la lógica de este bloque.
            const ls = isThumbnail ? 1 : 1.5;
            // Valor intermedio `shieldContentScale` utilizado por la lógica de este bloque.
            const shieldContentScale = useAdminHorizontalLayout
              ? shieldCfg.width / config.reversoShield.width
              : 1;
            // Valor intermedio `cnWidth` utilizado por la lógica de este bloque.
            const cnWidth = cnCfg.width * scale * shieldContentScale;
            // Valor intermedio `cnFont` utilizado por la lógica de este bloque.
            const cnFont = cnCfg.fontSize * scale * shieldContentScale;
            // Envoltura automática: el texto pasa al siguiente renglón cuando no
            // cabe en el ancho actual, en función del tamaño de la letra. Si la
            // letra crece, caben menos caracteres por línea y se generan más
            // renglones. Nunca se recorta: como red de seguridad, CurvedText
            // encoge ligeramente una línea si una sola palabra la desborda.
            const lines = wrapTextByWidth(cnText, cnFont, cnWidth, ls);
            // Valor intermedio `lineH` utilizado por la lógica de este bloque.
            const lineH = cnCfg.height * scale * shieldContentScale;
            // Valor intermedio `lineGap` utilizado por la lógica de este bloque.
            const lineGap = (cnCfg.lineGap ?? 0) * scale * shieldContentScale;
            return (
              <div
                className="reverso-company-name"
                style={{
                  position: 'absolute',
                  top: cnCfg.top,
                  left: cnCfg.left,
                  transform: `translate(-50%, -50%) rotate(${shieldCfg.rotate}deg)`,
                  transformOrigin: 'center center',
                  zIndex: 10,
                  pointerEvents: 'none',
                  userSelect: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: `${cnWidth}px`,
                }}
              >
                {lines.map((line, i) => (
                  <CurvedText
                    key={`${line}-${i}`}
                    text={line}
                    width={cnWidth}
                    height={lineH}
                    fontFamily='"Dollar Bill", impact, sans-serif'
                    curve="up"
                    fontSize={cnFont}
                    color={textColor}
                    curveIntensity={cnCfg.curveIntensity}
                    letterSpacing={ls}
                    maxCharsPerLine={999}
                    lineSpacing={cnCfg.lineSpacing}
                    showOutline={true}
                    outlineWidth={0.6}
                    outlineColor="#ffffff"
                    showShadow={true}
                    shadowOffsetX={2}
                    shadowOffsetY={2}
                    shadowOpacity={0.55}
                    style={{ flexShrink: 0, marginBottom: i === lines.length - 1 ? 0 : `${lineGap}px` }}
                  />
                ))}
              </div>
            );
          })()}

        </div>

        {/* Texto del reverso FUERA del wrapper rotado 180°: así tiene una SOLA
            rotación y el motor de impresión de Chrome sí renderiza los títulos
            (rotaciones anidadas los descartaban). ReversoTextOverlay ya compensa
            el giro y la posición en vertical. */}
        <ReversoTextOverlay
          reversoOverride={reversoOverride}
          orientation={orientation}
          isThumbnail={isThumbnail}
          adminEditorPreview={adminEditorPreview}
        />

        {!isThumbnail && (
          <div
            style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 100, backgroundColor: 'transparent' }}
            onContextMenu={(e) => e.preventDefault()}
          />
        )}
      </div>
    );
  }

  // Componente `CompanyTitle` que renderiza esta parte de la interfaz.
  const CompanyTitle = () => (
    <div
      style={{
        position: 'absolute', zIndex: 25,
        top: `${config.companyName.top + previewTitleNudge}%`, left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        pointerEvents: 'none',
        width: `${config.companyName.width * scale}px`,
        maxWidth: '90%',
        fontFamily: '"Dollar Bill", impact, sans-serif',
      }}
    >
      <CurvedText
        text={decodeHtmlEntities(boletaData.companyName)?.toUpperCase() || 'NOMBRE DE EMPRESA'}
        width={config.companyName.width * scale}
        height={config.companyName.height * scale}
        fontFamily='"Dollar Bill", impact, sans-serif'
        curve="up"
        fontSize={config.companyName.fontSize * scale}
        color={textColor}
        curveIntensity={config.companyName.curveIntensity}
        letterSpacing={isThumbnail ? 1 : 1.5}
        maxCharsPerLine={config.companyName.maxCharsPerLine}
        lineSpacing={config.companyName.lineSpacing}
        showOutline={true}
        outlineWidth={0.6}
        outlineColor="#ffffff"
        showShadow={true}
        shadowOffsetX={2}
        shadowOffsetY={2}
        shadowOpacity={0.55}
      />
    </div>
  );

  // Componente `FixedDecorativeLayers` que renderiza esta parte de la interfaz.
  const FixedDecorativeLayers = () => {
    // Valor intermedio `rot` utilizado por la lógica de este bloque.
    const rot = isHorizontal ? -90 : 0;
    // Valor intermedio `lScale` utilizado por la lógica de este bloque.
    const lScale = layerScale;

    // Valor intermedio `wScale` utilizado por la lógica de este bloque.
    const wScale = isHorizontal ? hDecorWidthScale : lScale;
    // Valor intermedio `hScale` utilizado por la lógica de este bloque.
    const hScale = isHorizontal ? hDecorHeightScale : lScale;
    // Valor intermedio `horizontalWaveWidth` utilizado por la lógica de este bloque.
    const horizontalWaveWidth = useAdminHorizontalLayout
      ? ADMIN_HORIZONTAL_PREVIEW_LAYOUT.waveWidth
      : 75;

    // Valor intermedio `designNum` utilizado por la lógica de este bloque.
    const designNum = boletaData?.designId?.match(/\d+/) ? boletaData.designId.match(/\d+/)[0] : '1';
    // Valor intermedio `dynamicDifuminado` utilizado por la lógica de este bloque.
    const dynamicDifuminado = `${SVG_BASE}/difuminados/marco_${designNum}.svg`;

    return (
      <>
        <RotatableLayer src={FIXED_LAYERS.base} zIndex={0} widthScale={wScale} heightScale={hScale} rotate={rot} />
        {logoUrl && (
          <RotatableLayer src={logoUrl} zIndex={1} scale={lScale} rotate={0} />
        )}
          {isHorizontal ? (
            <>
              <RotatableLayer
                src={FIXED_LAYERS.innerDecorHorizontal}
                zIndex={2}
                widthScale={horizontalWaveWidth}
                heightScale={113}
                offsetY={0}
                offsetX={0}
                // 3_H.svg ya está orientado para la boleta horizontal.
                // El giro exterior de impresión se aplica después.
                rotate={0}
              />
              {!isThumbnail && (
                <RotatableHueLayer
                  src={FIXED_LAYERS.innerDecorHorizontal}
                  zIndex={3}
                  widthScale={horizontalWaveWidth}
                  heightScale={113}
                  offsetY={0}
                  offsetX={0}
                  rotate={0}
                  className="holographic-wave-hue"
                />
              )}
            </>
          ) : (
            <>
              <RotatableLayer
                src={FIXED_LAYERS.innerDecor}
                zIndex={1}
                scale={lScale}
                rotate={0}
              />
              {!isThumbnail && (
                <RotatableHueLayer
                  src={FIXED_LAYERS.innerDecor}
                  zIndex={2}
                  scale={lScale}
                  rotate={0}
                  className="holographic-wave-hue"
                />
              )}
            </>
          )}
        <RotatableLayer src={FIXED_LAYERS.glowBorder} zIndex={2} widthScale={wScale} heightScale={hScale} rotate={rot} />
        
        <RotatableLayer src={dynamicDifuminado} zIndex={4} widthScale={wScale} heightScale={hScale} rotate={rot} />
        
        {!isHorizontal && (
          <RotatableLayer src={FIXED_LAYERS.cityscape2} zIndex={7} scale={lScale} rotate={0} />
        )}
        
        <RotatableLayer src={FIXED_LAYERS.sealCircle} zIndex={25} widthScale={wScale} heightScale={hScale} rotate={rot} />
        <RotatableHueLayer
          src={FIXED_LAYERS.sealCircle}
          zIndex={26} widthScale={wScale} heightScale={hScale} rotate={rot}
          className="holographic-hue-rotate"
        />
        
        {membreteUrl && (
          <RotatableLayer 
            src={membreteUrl} 
            zIndex={10} 
            widthScale={lScale * 0.85} 
            heightScale={lScale} 
            offsetY={-10}
            offsetX={config.membretes.offsetX || 0}
            rotate={0} 
          />
        )}

        <RotatableLayer 
          src={FIXED_LAYERS.dataFields} 
          zIndex={15} 
          widthScale={lScale * 0.85} 
          heightScale={lScale} 
          offsetY={-10}
          offsetX={config.membretes.offsetX || 0}
          rotate={0} 
        />
      </>
    );
  };

  return (
    <div
      style={containerStyle}
      className={`protected-content boleta-container-animated ${isHorizontal ? 'is-horizontal' : 'is-vertical'}`}
      onContextMenu={(e) => e.preventDefault()}
    >
      <style>
        {`
          @keyframes holographic-animation {
            0% { filter: hue-rotate(0deg) brightness(1.2) saturate(1.2); opacity: 0.5; }
            50% { filter: hue-rotate(180deg) brightness(1.5) saturate(1.5); opacity: 0.8; }
            100% { filter: hue-rotate(360deg) brightness(1.2) saturate(1.2); opacity: 0.5; }
          }
          .holographic-hue-rotate {
            animation: holographic-animation 4s infinite linear;
            mix-blend-mode: color-dodge !important;
          }
          @media print {
            .boleta-container-animated { overflow: visible !important; clip-path: none !important; isolation: isolate !important; background: white !important; }
            .boleta-print-container { overflow: visible !important; }
            .half { overflow: visible !important; }
            .print-sheet { overflow: visible !important; }
            .reverso-content-wrapper { overflow: visible !important; }
            .reverso-paragraph-block { overflow: visible !important; }
            .reverso-paragraph-scroll { overflow: visible !important; }
            .wisby-force-print-img { opacity: 1 !important; visibility: visible !important; mix-blend-mode: normal !important; filter: none !important; transition: none !important; }
            svg, text, span, p, div { filter: none !important; -webkit-filter: none !important; text-shadow: none !important; box-shadow: none !important; }
          }
        `}
      </style>
      
      <FixedDecorativeLayers />
      
      <ProtectedImg
        src={marcoUrl}
        alt="Marco"
        style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: `translate(-50%, -50%) rotate(${isHorizontal ? -90 : 0}deg)`,
          width: `${isHorizontal ? hDecorWidthScale : config.marco.scale}%`,
          height: `${isHorizontal ? hDecorHeightScale : config.marco.scale}%`,
          objectFit: 'fill', zIndex: 3,
        }}
      />
      
      <CompanyTitle />

      {/* MembretesOverlay dibuja los 4 valores con el formateo automático */}
      <MembretesOverlay
        adminFields={adminFields}
        orientation={orientation}
        isThumbnail={isThumbnail}
      />

      <DynamicTextOverlay
        adminFields={adminFields}
        series={series}
        textoLegalOverride={textoLegalOverride}
        orientation={orientation}
        isThumbnail={isThumbnail}
        adminEditorPreview={adminEditorPreview}
      />
      {!isThumbnail && (
        <div
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 100, backgroundColor: 'transparent' }}
          onContextMenu={(e) => e.preventDefault()}
        />
      )}
    </div>
  );
}

// Valor intermedio `BoletaPreviewScaled` utilizado por la lógica de este bloque.
export const BoletaPreviewScaled = memo(function BoletaPreviewScaled({
  boletaData,
  designUrls,
  adminFields,
  series,
  textoLegalOverride,
  reversoOverride,
  showBack = false,
  isThumbnail = false,
  fitMode = 'contain',
  zoom = 1,
  className = '',
  style = {},
  boletaCode,
}) {
  // Referencia mutable `outerRef` que conserva datos entre renderizados sin provocar uno nuevo.
  const outerRef = useRef(null);
  // Estado local `containerSize` y su actualizador, usados para reflejar cambios en la interfaz.
  const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });

  // Callback `measure` memoizado para conservar su referencia entre renderizados.
  const measure = useCallback(() => {
    if (!outerRef.current) return;
    // offsetWidth/offsetHeight NO se ven afectados por el transform: scale()
    // del lienzo padre; así conserva su tamaño real dentro de la distribución
    // y no hereda el zoom de la vista anterior al cambiar de vista previa.
    const w = outerRef.current.offsetWidth;
    // Valor intermedio `h` utilizado por la lógica de este bloque.
    const h = outerRef.current.offsetHeight;
    setContainerSize((prev) => {
      if (Math.abs(prev.w - w) < 1 && Math.abs(prev.h - h) < 1) return prev;
      return { w, h };
    });
  },[]);

  useEffect(() => {
    measure();
    // Valor intermedio `ro` utilizado por la lógica de este bloque.
    const ro = new ResizeObserver(measure);
    if (outerRef.current) ro.observe(outerRef.current);
    return () => ro.disconnect();
  }, [measure]);

  // Valor intermedio `orientation` utilizado por la lógica de este bloque.
  const orientation = boletaData?.orientation || 'vertical';
  // Valor intermedio `canon` utilizado por la lógica de este bloque.
  const canon = CANONICAL[orientation];

  // Valor intermedio `visibleCanvasW` utilizado por la lógica de este bloque.
  const visibleCanvasW = canon.width;
  // Valor intermedio `visibleCanvasH` utilizado por la lógica de este bloque.
  const visibleCanvasH = canon.height;

  // Valor intermedio `computedScale` utilizado por la lógica de este bloque.
  let computedScale = 1;
  if (containerSize.w > 0 && containerSize.h > 0) {
    if (fitMode === 'width') {
      computedScale = containerSize.w / visibleCanvasW;
    } else {
      computedScale = Math.min(
        containerSize.w / visibleCanvasW,
        containerSize.h / visibleCanvasH
      );
    }
  }
  computedScale *= zoom;

  // Valor intermedio `displayW` utilizado por la lógica de este bloque.
  const displayW = visibleCanvasW * computedScale;
  // Valor intermedio `displayH` utilizado por la lógica de este bloque.
  const displayH = visibleCanvasH * computedScale;

  return (
    <div
      ref={outerRef}
      className={className}
      style={{
        width: '100%', height: '100%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden',
        ...style,
      }}
    >
      {containerSize.w > 0 && (
        <div
          style={{
            width: displayW,
            height: displayH,
            flexShrink: 0,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: canon.width,
              height: canon.height,
              transformOrigin: 'top left',
              transform: `scale(${computedScale})`,
              position: 'absolute',
              top: 0,
              left: 0,
            }}
          >
            <BoletaPreview
              boletaData={boletaData}
              designUrls={designUrls}
              adminFields={adminFields}
              series={series}
              textoLegalOverride={textoLegalOverride}
              reversoOverride={reversoOverride}
              showBack={showBack}
              isThumbnail={isThumbnail}
              boletaCode={boletaCode}
            />
          </div>
        </div>
      )}
    </div>
  );
});

export default memo(BoletaPreview, (prevProps, nextProps) => {
  // Valor intermedio `prev` utilizado por la lógica de este bloque.
  const prev = prevProps.boletaData;
  // Valor intermedio `next` utilizado por la lógica de este bloque.
  const next = nextProps.boletaData;
  if (!prev || !next) return false;
  return (
    prev.companyName === next.companyName &&
    prev.orientation === next.orientation &&
    prev.designId === next.designId &&
    prev.textFont === next.textFont &&
    prev.textSize === next.textSize &&
    prev.logoId === next.logoId &&
    prev.membreteId === next.membreteId &&
    prev.sealId === next.sealId &&
    prev.sealColor === next.sealColor &&
    prevProps.designUrls?.marco === nextProps.designUrls?.marco &&
    prevProps.designUrls?.logo === nextProps.designUrls?.logo &&
    prevProps.designUrls?.membrete === nextProps.designUrls?.membrete &&
    prevProps.isThumbnail === nextProps.isThumbnail &&
    prevProps.showBack === nextProps.showBack &&
    prevProps.boletaCode === nextProps.boletaCode &&
    JSON.stringify(prevProps.series) === JSON.stringify(nextProps.series) &&
    JSON.stringify(prevProps.adminFields) === JSON.stringify(nextProps.adminFields) &&
    JSON.stringify(prevProps.textoLegalOverride) === JSON.stringify(nextProps.textoLegalOverride) &&
    JSON.stringify(prevProps.reversoOverride) === JSON.stringify(nextProps.reversoOverride)
  );
});
